import React, { useCallback, useState } from 'react';
import ReactFlow, { Background } from 'reactflow';
import { CanvasContextMenu } from '@components/MainStage/ContextMenu/CanvasContextMenu';
import { CustomControls } from '@components/MainStage/CustomControls';
import { CustomEdge } from '@components/MainStage/CustomEdge';
import { CustomNode } from '@components/MainStage/CustomNode';
import useCanvasContextMenu from '@components/MainStage/hooks/stage/useCanvasContextMenu';
import useTranslateExtent from '@components/MainStage/hooks/stage/useTranslateExtent';
import useEdgeLayers from '@components/MainStage/hooks/useEdgeLayers';
import useFilteredCanvas from '@components/MainStage/hooks/useFilteredCanvas';
import { LayerNode } from '@components/MainStage/LayerNode';
import { BlockBackground } from '@components/MainStage/MainStage.styled';
import { NewNode } from '@components/MainStage/NewNode';
import { SubLayerNode } from '@components/MainStage/SubLayerNode';
import { MenuState } from '@components/MainStage/types';
import {
  canvasStageStyles,
  INITIAL_VIEWPORT,
  MAX_ZOOM,
  MIN_ZOOM,
} from '@constants/canvas/general';
import { ReactFlowId } from '@constants/entities/reports';
import { NodesDataProvider } from '@context/NodesDataContext';
import { useProject } from '@context/Project/ProjectProvider';
import useCanvasZoom from '@hooks/canvas/useCanvasZoom';
import useCanvasZoomController from '@hooks/canvas/useCanvasZoomController';
import useToggle from '@hooks/useToggle';
import { Box } from '@mui/material';
import { InsightStageStyled } from '@views/Insights/Insights.styled';
import useEdgeClick from '@views/Insights/Stage/hooks/stage/useEdgeClick';
import useNodeClick from '@views/Insights/Stage/hooks/stage/useNodeClick';
import usePaneClick from '@views/Insights/Stage/hooks/stage/usePaneClick';
import { Tabs } from '@views/Insights/types';

const nodeTypes = {
  customNode: CustomNode,
  newNode: NewNode,
  layerNode: LayerNode,
  subLayerNode: SubLayerNode,
};

const edgeTypes = {
  customEdge: CustomEdge,
};

export const Stage = () => {
  const { handleCloseDrawer, hasChanges, insightsTab: type } = useProject();
  const { nodes, edges: filteredEdges } = useFilteredCanvas();
  const edges = useEdgeLayers(filteredEdges);
  const { zoom } = useCanvasZoom();

  const [menu, setMenu] = useState<MenuState | null>(null);
  const [isMenuOpen, { off: closeMenu, on: openMenu }] = useToggle();

  const { onCanvasContextMenu } = useCanvasContextMenu({ openMenu, setMenu });

  const handlePaneClick = usePaneClick();

  const onPaneClick = useCallback(() => {
    closeMenu();
    handlePaneClick();
  }, [closeMenu, handlePaneClick]);

  const onMove = useCallback(() => {
    if (menu) closeMenu();
  }, [menu, closeMenu]);

  const onEdgeClick = useEdgeClick(onPaneClick);
  const onNodeClick = useNodeClick(onPaneClick);
  const onNodeDragStart = () => {};
  const onNodeDragStop = () => {};

  const onBlockBackClick = () => {
    if (type === Tabs.Dependency) {
      handleCloseDrawer();
    } else if (type === Tabs.FocusOn) {
      handleCloseDrawer(undefined, false);
    }
  };

  useCanvasZoomController();
  const translateExtent = useTranslateExtent();

  return (
    <Box id="canvas" position="relative">
      {hasChanges && <BlockBackground onClick={onBlockBackClick} />}

      <InsightStageStyled>
        <NodesDataProvider>
          <ReactFlow
            id={ReactFlowId}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            elevateNodesOnSelect
            selectNodesOnDrag={false}
            onMove={onMove}
            onPaneClick={onPaneClick}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneContextMenu={onCanvasContextMenu}
            onNodeContextMenu={onCanvasContextMenu}
            onEdgeContextMenu={onCanvasContextMenu}
            onNodeDragStart={onNodeDragStart}
            onNodeDragStop={onNodeDragStop}
            style={canvasStageStyles}
            multiSelectionKeyCode={null}
            deleteKeyCode={null}
            translateExtent={translateExtent}
            zoomOnDoubleClick={false}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            zoomOnPinch={false}
            defaultViewport={INITIAL_VIEWPORT(zoom)}
          >
            <Background />

            <CanvasContextMenu
              isOpen={isMenuOpen}
              onClose={closeMenu}
              menu={menu}
            />

            <CustomControls />
          </ReactFlow>
        </NodesDataProvider>
      </InsightStageStyled>
    </Box>
  );
};
